import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fmt } from "@/lib/utils-msip";
import { toast } from "sonner";
import { CheckCircle2, PauseCircle, Trash2, Zap } from "lucide-react";

export const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
export const fmtDay = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export function SourceBadge({ h }) {
  if (h.source !== "api") return <span className="text-[10px] text-muted-foreground">manual</span>;
  return <span className="inline-flex items-center gap-1 rounded bg-foreground text-background px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider" title={`Pushed by API key "${h.api_key_name}"`} data-testid={`upload-source-api-${h.id}`}><Zap size={9} /> API</span>;
}

const STATUS = {
  committed: ["Loaded", "bg-emerald-50 text-emerald-800"],
  review: ["Held for review", "bg-amber-50 text-amber-800"],
  rejected: ["Rejected", "bg-red-50 text-red-800"],
  superseded: ["Superseded", "bg-secondary text-muted-foreground"],
  discarded: ["Discarded", "bg-secondary text-muted-foreground"],
};
export function StatusBadge({ status, testId }) {
  const [label, cls] = STATUS[status] || [status, "bg-secondary text-muted-foreground"];
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap ${cls}`} data-testid={testId}>{label}</span>;
}

// One held file: why it was held, what it contains, Approve / Discard.
function HeldCard({ h, onDone }) {
  const [busy, setBusy] = useState(null);
  const act = async (what) => {
    setBusy(what);
    try {
      const { data } = await api.post(`/uploads/${h.id}/${what}`);
      toast.success(what === "commit" ? `Loaded ${h.file_name}: ${data.inserted} inserted, ${data.replaced} replaced` : `${h.file_name} discarded`);
      onDone(what, data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50/60 p-4 space-y-3" data-testid={`held-upload-${h.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <PauseCircle size={16} className="text-amber-700" />
        <span className="font-semibold text-sm" data-testid={`held-upload-file-${h.id}`}>{h.file_name}</span>
        <SourceBadge h={h} />
        <span className="text-xs text-muted-foreground">as on <b className="num text-foreground">{fmtDay(h.as_on)}</b> · received {fmtWhen(h.timestamp)} · by {h.uploaded_by}</span>
      </div>
      {h.hold_reasons?.length > 0 && (
        <ul className="text-xs text-amber-900 space-y-0.5" data-testid={`held-upload-reasons-${h.id}`}>
          {h.hold_reasons.map((r, i) => <li key={i}>• {r}</li>)}
        </ul>
      )}
      {(!h.hold_reasons || h.hold_reasons.length === 0) && <p className="text-xs text-amber-900">Parsed and waiting for confirmation.</p>}
      <div className="overflow-auto border rounded bg-card">
        <table className="w-full text-xs">
          <thead className="border-b"><tr>{["Sheet", "Scope", "Date", "RTOs", "Kerala total", "File KERALA"].map((x) => <th key={x} className="px-2 py-1 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{x}</th>)}</tr></thead>
          <tbody>
            {(h.sheets || []).map((s) => (
              <tr key={s.name} className="border-b last:border-0">
                <td className="px-2 py-1 font-medium">{s.name}</td><td className="px-2 py-1">{s.scope}</td><td className="px-2 py-1 num">{s.date}</td>
                <td className="px-2 py-1 num">{s.rows}</td><td className="px-2 py-1 num">{fmt(s.kerala_total)}</td><td className="px-2 py-1 num">{fmt(s.kerala_file_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        {h.new_rtos?.length > 0 && <span data-testid={`held-upload-newrtos-${h.id}`}><b className="text-foreground">New RTOs ({h.new_rtos.length}):</b> {h.new_rtos.slice(0, 12).join(", ")}{h.new_rtos.length > 12 ? "…" : ""} — created on approval</span>}
        {h.duplicate_dates?.length > 0 && <span><b className="text-foreground">Replaces:</b> {h.duplicate_dates.join(", ")}</span>}
        {h.warnings?.length > 0 && <span>{h.warnings.length} parser warning{h.warnings.length === 1 ? "" : "s"}</span>}
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => act("commit")} disabled={!!busy} data-testid={`held-upload-approve-${h.id}`}><CheckCircle2 size={14} className="mr-1" /> {busy === "commit" ? "Loading…" : "Approve & load"}</Button>
        <Button size="sm" variant="outline" onClick={() => act("discard")} disabled={!!busy} data-testid={`held-upload-discard-${h.id}`}><Trash2 size={14} className="mr-1" /> Discard</Button>
      </div>
    </div>
  );
}

export function HeldUploads({ items, excludeId, onDone }) {
  const held = items.filter((h) => h.status === "review" && h.id !== excludeId);
  if (!held.length) return null;
  return (
    <div className="space-y-3" data-testid="held-uploads">
      <h3 className="font-heading text-sm font-semibold">Held for review <span className="text-muted-foreground font-normal text-xs">· {held.length} file{held.length === 1 ? "" : "s"} waiting — approve to load, or discard and ask for a re-send</span></h3>
      {held.map((h) => <HeldCard key={h.id} h={h} onDone={onDone} />)}
    </div>
  );
}
